// Opening an invite link: set up a passkey on your own phone or laptop.
// The invite's secret is in the part of the address after "#", which browsers never
// send to any server; this page posts it to the console API itself.

import { useEffect, useState } from 'react';
import { api, message } from '../api.ts';
import { createPasskey, passkeyProblem, passkeysAvailable, type CreateOptions } from '../passkey.ts';
import { Link, Loading } from '../ui.tsx';

type Details = { name: string; kind: 'staff' };

function guessDevice(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android phone';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  return 'Browser';
}

export function Invite() {
  // Read once: the address is cleaned up after the passkey is set up.
  const [token] = useState(() => location.hash.replace(/^#(t=)?/, ''));
  const [details, setDetails] = useState<Details | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setDetails(null);
      return;
    }
    api<Details>('POST', '/invites/details', { token }).then(setDetails, (e) => {
      setDetails(null);
      setError(message(e));
    });
  }, [token]);

  const setUp = async () => {
    setBusy(true);
    setError(null);
    try {
      const options = await api<CreateOptions>('POST', '/invites/options', { token });
      let response;
      try {
        response = await createPasskey(options);
      } catch (e) {
        setError(passkeyProblem(e));
        return;
      }
      await api('POST', '/invites/register', { token, response, device: guessDevice() });
      history.replaceState(null, '', '/invite'); // the secret is used up; take it out of the address
      setDone(true);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="center">
      <div className="solo">
        <div className="brand">
          <span className="brand-mark">Z.A.S.</span>
          <span className="faint">Console</span>
        </div>
        {details === undefined && <Loading />}
        {details === null && !done && (
          <>
            <h1>This link doesn't work</h1>
            <p className="muted">{error ?? 'It may have expired or already been used.'} Ask the Z.A.S. team for a new one.</p>
          </>
        )}
        {details && !done && (
          <>
            <h1>Hello {details.name}</h1>
            <p className="muted">You've been added to the Z.A.S. console. Set up a passkey on this phone or laptop: it uses the Face ID, fingerprint or PIN you already have. There's no password.</p>
            {passkeysAvailable() ? (
              <button className="btn primary" onClick={setUp} disabled={busy}>
                {busy ? 'Waiting for your device…' : 'Set up my passkey'}
              </button>
            ) : (
              <p className="error">This browser can't use passkeys. Try an up-to-date Safari, Chrome or Edge.</p>
            )}
            {error && (
              <p className="error" role="alert">
                {error}
              </p>
            )}
          </>
        )}
        {done && details && (
          <>
            <h1>You're set up</h1>
            <p className="muted">You can now sign in with this passkey.</p>
            <Link to="/" className="btn primary">
              Sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
